import type {
  ErpEntity,
  ErpInboxRow,
  ErpOutboxRow,
  ErpStatus,
  ErpWebhookOverview,
  ListResponse,
} from '@warehouse/shared';

import { api } from '../../lib/api-client';
import { listQueryParams, type ListState } from '../../lib/list-query';
import { commandId } from '../../lib/command-id';

/**
 * The bc-integration area's data access. One module, one file — every request
 * this area makes is visible here, and the query keys live next to the calls
 * that fill them so `useLiveQuery` invalidation cannot drift.
 */

export const erpKeys = {
  status: ['erp', 'status'] as const,
  webhooks: ['erp', 'webhooks'] as const,
  outbox: (state: ListState) => ['erp', 'outbox', listQueryParams(state)] as const,
  inbox: (state: ListState) => ['erp', 'inbox', listQueryParams(state)] as const,
};

export function fetchErpStatus(): Promise<ErpStatus> {
  return api.get<ErpStatus>('/erp/status');
}

export function fetchWebhookOverview(): Promise<ErpWebhookOverview> {
  return api.get<ErpWebhookOverview>('/erp/webhooks');
}

export function fetchOutbox(state: ListState): Promise<ListResponse<ErpOutboxRow>> {
  return api.get<ListResponse<ErpOutboxRow>>('/erp/outbox', { query: listQueryParams(state) });
}

export function fetchInbox(state: ListState): Promise<ListResponse<ErpInboxRow>> {
  return api.get<ListResponse<ErpInboxRow>>('/erp/inbox', { query: listQueryParams(state) });
}

/**
 * `commandId` is generated per user action, so a double-clicked button is one
 * command server-side (`command_dedupe`) rather than two.
 */
export function resyncEntity(entity: ErpEntity, mode: 'delta' | 'full'): Promise<unknown> {
  return api.post('/erp/resync', { commandId: commandId(), payload: { entity, mode } });
}

export function retryOutbox(id: string): Promise<unknown> {
  return api.post(`/erp/outbox/${id}/retry`, { commandId: commandId(), payload: {} });
}

export function cancelOutbox(id: string, reason?: string): Promise<unknown> {
  return api.post(`/erp/outbox/${id}/cancel`, {
    commandId: commandId(),
    payload: reason ? { reason } : {},
  });
}

export function retryInbox(id: string): Promise<unknown> {
  return api.post(`/erp/inbox/${id}/retry`, { commandId: commandId(), payload: {} });
}

export function renewWebhooks(): Promise<unknown> {
  return api.post('/erp/webhooks/renew', { commandId: commandId(), payload: {} });
}

export function drainQueues(): Promise<unknown> {
  return api.post('/erp/drain', {});
}
